import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { Contact } from '../../types/intellijetnet'

export class ContactsService {
  private static readonly TABLE_NAME = 'contacts'

  /**
   * Get query builder for contacts
   */
  static query(): QueryBuilder {
    return intelliJetNetService.from(this.TABLE_NAME)
  }

  /**
   * Get contact by ID
   */
  static async getById(contactId: number): Promise<Contact | null> {
    const { data } = await this.query()
      .eq('contact_id', contactId)
      .execute<Contact>()
    return data[0] || null
  }

  /**
   * Search contacts by name
   */
  static async searchByName(searchTerm: string): Promise<Contact[]> {
    const { data } = await this.query()
      .or(
        [
          { column: 'first_name', operator: 'ilike', value: `%${searchTerm}%` },
          { column: 'last_name', operator: 'ilike', value: `%${searchTerm}%` },
          { column: 'full_name', operator: 'ilike', value: `%${searchTerm}%` }
        ]
      )
      .eq('is_active', true)
      .order('last_name')
      .order('first_name')
      .execute<Contact>()
    return data
  }

  /**
   * Get contacts by email
   */
  static async getByEmail(email: string): Promise<Contact | null> {
    const { data } = await this.query()
      .eq('email', email.toLowerCase())
      .eq('is_active', true)
      .execute<Contact>()
    return data[0] || null
  }

  /**
   * Get contacts by company
   */
  static async getByCompanyId(companyId: number): Promise<Contact[]> {
    const { data } = await this.query()
      .contains('company_ids', [companyId])
      .eq('is_active', true)
      .order('last_name')
      .order('first_name')
      .execute<Contact>()
    return data
  }

  /**
   * Get contacts by aircraft
   */
  static async getByAircraftId(aircraftId: number): Promise<Contact[]> {
    const { data } = await this.query()
      .contains('aircraft_ids', [aircraftId])
      .eq('is_active', true)
      .execute<Contact>()
    return data
  }

  /**
   * Get contacts by title/role
   */
  static async getByTitle(title: string): Promise<Contact[]> {
    const { data } = await this.query()
      .ilike('title', `%${title}%`)
      .eq('is_active', true)
      .order('last_name')
      .order('first_name')
      .execute<Contact>()
    return data
  }

  /**
   * Get executive contacts (C-level, VP, Director)
   */
  static async getExecutives(): Promise<Contact[]> {
    const { data } = await this.query()
      .or(
        [
          { column: 'title', operator: 'ilike', value: '%CEO%' },
          { column: 'title', operator: 'ilike', value: '%CFO%' },
          { column: 'title', operator: 'ilike', value: '%COO%' },
          { column: 'title', operator: 'ilike', value: '%CTO%' },
          { column: 'title', operator: 'ilike', value: '%President%' },
          { column: 'title', operator: 'ilike', value: '%Vice President%' },
          { column: 'title', operator: 'ilike', value: '%VP %' },
          { column: 'title', operator: 'ilike', value: '%Director%' }
        ]
      )
      .eq('is_active', true)
      .order('last_name')
      .execute<Contact>()
    return data
  }

  /**
   * Get contacts with pagination
   */
  static async getPaginated(params: {
    page?: number
    limit?: number
    sortBy?: 'full_name' | 'last_name' | 'created_at' | 'updated_at'
    sortOrder?: 'asc' | 'desc'
    search?: string
    companyId?: number
  } = {}): Promise<{ data: Contact[]; total: number }> {
    const page = params.page || 1
    const limit = params.limit || 50
    const offset = (page - 1) * limit
    const sortBy = params.sortBy || 'last_name'
    const sortOrder = params.sortOrder || 'asc'

    let query = this.query()
      .eq('is_active', true)
      .order(sortBy, sortOrder === 'asc')
      .limit(limit)
      .offset(offset)
      .count('exact')

    if (params.search) {
      query = query.or(
        [
          { column: 'first_name', operator: 'ilike', value: `%${params.search}%` },
          { column: 'last_name', operator: 'ilike', value: `%${params.search}%` },
          { column: 'full_name', operator: 'ilike', value: `%${params.search}%` },
          { column: 'email', operator: 'ilike', value: `%${params.search}%` }
        ]
      )
    }

    if (params.companyId) {
      query = query.contains('company_ids', [params.companyId])
    }

    const result = await query.execute<Contact>()
    return {
      data: result.data,
      total: result.count || 0
    }
  }

  /**
   * Get contact statistics
   */
  static async getStatistics(): Promise<{
    total: number
    withEmail: number
    byTitle: Record<string, number>
    executives: number
  }> {
    const { count: total } = await this.query()
      .eq('is_active', true)
      .count('exact')
      .execute<Contact>()

    const { count: withEmail } = await this.query()
      .eq('is_active', true)
      .neq('email', null)
      .count('exact')
      .execute<Contact>()

    const executives = await this.getExecutives()

    // Get title distribution (would need an RPC function for efficient grouping)
    const titleData = await intelliJetNetService
      .rpc('get_contacts_by_title_count') as Array<{title: string, count: number}> | null

    return {
      total: total || 0,
      withEmail: withEmail || 0,
      executives: executives.length,
      byTitle: titleData?.reduce((acc, item) => {
        acc[item.title] = item.count
        return acc
      }, {} as Record<string, number>) || {}
    }
  }

  /**
   * Get recently updated contacts
   */
  static async getRecentlyUpdated(days = 7): Promise<Contact[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)

    const { data } = await this.query()
      .gte('updated_at', date.toISOString())
      .eq('is_active', true)
      .order('updated_at', false)
      .limit(100)
      .execute<Contact>()
    return data
  }

  /**
   * Get contacts without companies
   */
  static async getUnaffiliated(): Promise<Contact[]> {
    const { data } = await this.query()
      .eq('company_ids', '{}')
      .eq('is_active', true)
      .order('last_name')
      .execute<Contact>()
    return data
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
}

export default ContactsService